import React, { useState, useEffect, useRef } from 'react';
import { visualStyles } from '../../data/styleOptions';
import { uploadWorldObjectImage } from '../../services/firebase/storage';

export function WorldObjectModal({
  isOpen,
  objectType,
  object,
  onClose,
  onSave,
  onDelete,
  onGenerateImage,
  generatingImageUid,
  GENERATION_COST,
  currentBook,
}) {
  const [activeTab, setActiveTab] = useState('image');
  const [formData, setFormData] = useState(object || {});
  const [hasChanges, setHasChanges] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [showArtStyleModal, setShowArtStyleModal] = useState(false);
  const [selectedArtStyle, setSelectedArtStyle] = useState(null);
  const [showAddAttributeModal, setShowAddAttributeModal] = useState(false);
  const [newAttributeName, setNewAttributeName] = useState('');
  const [newAttributeValue, setNewAttributeValue] = useState('');
  const [addAttributeContext, setAddAttributeContext] = useState(null);
  const attributesScrollRef = useRef(null);
  const [lastAddedAttributeKey, setLastAddedAttributeKey] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setFormData(object || {});
    setHasChanges(false);
    setImageLoadError(false);
    setUploadError(null);
    setActiveTab('image');
    
    // Find the index of the current referenceImage in history
    if (object?.referenceImage && Array.isArray(object?.imageHistory)) {
      const currentIndex = object.imageHistory.findIndex(
        (img) => img.url === object.referenceImage
      );
      setHistoryIndex(currentIndex >= 0 ? currentIndex : 0);
    } else {
      setHistoryIndex(0);
    }
  }, [object, isOpen]);

  // Scroll to newly added attribute
  useEffect(() => {
    if (lastAddedAttributeKey && attributesScrollRef.current) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        const element = attributesScrollRef.current?.querySelector(
          `[data-attribute-key="${lastAddedAttributeKey}"]`
        );
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Highlight the newly added attribute briefly
          element.classList.add('bg-yellow-100');
          setTimeout(() => {
            element.classList.remove('bg-yellow-100');
          }, 2000);
        }
        setLastAddedAttributeKey(null);
      }, 0);
    }
  }, [lastAddedAttributeKey]);

  if (!object) return null;

  const getName = (obj) => {
    if (!obj) return '';
    return obj.name || obj.mainName || obj.nickName || obj.nickname || obj.Alias || obj.alias || obj.TrueName || obj.trueName || '';
  };

  const handleAttributeChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
    setHasChanges(true);
  };

  const handleNestedAttributeChange = (groupKey, fieldKey, value) => {
    setFormData((prev) => ({
      ...prev,
      [groupKey]: {
        ...(prev[groupKey] || {}),
        [fieldKey]: value,
      },
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    await onSave(object._uid || object.id, formData);
    setHasChanges(false);
  };

  const handleCancel = () => {
    setFormData(object || {});
    setHasChanges(false);
  };

  const handleImageError = () => {
    setImageLoadError(true);
  };

  const handleImageDelete = async () => {
    if (window.confirm('Delete this image?')) {
      await onSave(object._uid || object.id, {
        ...formData,
        referenceImage: null,
      });
      setHasChanges(false);
    }
  };

  const handleRestoreImage = async (imageUrl) => {
    await onSave(object._uid || object.id, {
      ...formData,
      referenceImage: imageUrl,
    });
    setHasChanges(false);
  };

  const handleCopyImageUrl = () => {
    if (object.referenceImage) {
      navigator.clipboard.writeText(object.referenceImage);
      alert('Image URL copied to clipboard');
    }
  };

  const handleFileSelect = async (event) => {
    console.log('handleFileSelect called', event);
    const file = event.target.files?.[0];
    console.log('Selected file:', file);
    if (!file) {
      console.log('No file selected');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      console.log('Uploading file with bookId:', currentBook._uid || currentBook.id);
      const result = await uploadWorldObjectImage(file, currentBook._uid || currentBook.id);
      
      if (result.error) {
        setUploadError(result.error);
        setUploading(false);
        return;
      }

      // Update the object with the uploaded image
      await onSave(object._uid || object.id, {
        ...formData,
        referenceImage: result.url,
        imageHistory: [
          ...(formData.imageHistory || []),
          { url: result.url, timestamp: Date.now() }
        ]
      });

      setUploading(false);
      setUploadError(null);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Upload failed');
      setUploading(false);
    }
  };

  const handleUploadClick = () => {
    console.log('handleUploadClick called, fileInputRef:', fileInputRef.current);
    fileInputRef.current?.click();
  };

  const handleGenerateImage = async () => {
    // Save any pending changes before generating
    if (hasChanges) {
      await handleSave();
    }
    setShowArtStyleModal(true);
  };

  const handleConfirmGenerate = () => {
    if (onGenerateImage) {
      onGenerateImage(object._uid || object.id, selectedArtStyle?.value);
    }
    setShowArtStyleModal(false);
    setSelectedArtStyle(null);
  };

  const openAddAttributeModal = (context = null) => {
    setAddAttributeContext(context);
    setNewAttributeName('');
    setNewAttributeValue('');
    setShowAddAttributeModal(true);
  };

  const getDefaultArtStyle = () => {
    const bookStyle = currentBook?.visualStyle?.value || currentBook?.visualStyle;
    if (!bookStyle) return null;
    return visualStyles.find((style) => style.value === bookStyle) || null;
  };

  const handleAddAttribute = () => {
    if (!newAttributeName.trim()) {
      alert('Attribute name is required');
      return;
    }

    if (addAttributeContext?.type === 'root') {
      // Add to root level
      handleAttributeChange(newAttributeName, newAttributeValue);
      setLastAddedAttributeKey(newAttributeName);
    } else if (addAttributeContext?.type === 'nested') {
      // Add to nested object
      handleNestedAttributeChange(addAttributeContext.groupKey, newAttributeName, newAttributeValue);
      // Scroll to the specific nested field
      setLastAddedAttributeKey(`nested-${addAttributeContext.groupKey}-${newAttributeName}`);
    } else if (addAttributeContext?.type === 'variation') {
      // Add to variation object
      const { variationIndex, groupKey } = addAttributeContext;
      const newArray = [...formData[groupKey]];
      newArray[variationIndex] = { ...newArray[variationIndex], [newAttributeName]: newAttributeValue };
      handleAttributeChange(groupKey, newArray);
      // Scroll to the specific variation field
      setLastAddedAttributeKey(`variation-${groupKey}-${variationIndex}-${newAttributeName}`);
    }

    setShowAddAttributeModal(false);
    setNewAttributeName('');
    setNewAttributeValue('');
    setAddAttributeContext(null);
  };

  const handleDeleteObject = async () => {
    if (window.confirm(`Delete ${getName(object)}?`)) {
      await onDelete(object._uid || object.id);
      onClose();
    }
  };

  const sortObjectKeys = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(sortObjectKeys);
    } else if (obj !== null && typeof obj === 'object') {
      return Object.keys(obj)
        .sort()
        .reduce((result, key) => {
          result[key] = sortObjectKeys(obj[key]);
          return result;
        }, {});
    }
    return obj;
  };


  if (!isOpen) return null;

  const imageHistory = object.imageHistory || [];
  const hasHistory = imageHistory.length > 0;

  return (
    <>
      {/* Hidden file input - always rendered */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        aria-label="Upload world object image"
      />
      
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}>
      <div className="bg-white rounded-lg shadow-xl w-[95vw] h-[95vh] max-w-7xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900">{getName(object) || 'Unnamed'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-3xl leading-none">
            ×
          </button>
        </div>

        {/* Top Section: Image and JSON side-by-side */}
        <div className="flex gap-4 px-6 py-4 border-b border-gray-200" style={{ height: '350px' }}>
          {/* Left: Current Image */}
          <div className="flex-1 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">
                {imageHistory.length > 0 ? `Image (${historyIndex + 1}/${imageHistory.length})` : 'Current Image'}
              </h3>
              {imageHistory.length > 1 && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setHistoryIndex((prev) => (prev - 1 + imageHistory.length) % imageHistory.length)}
                    className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-100 text-sm"
                    title="Previous image"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={() => setHistoryIndex((prev) => (prev + 1) % imageHistory.length)}
                    className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-100 text-sm"
                    title="Next image"
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
            <div className="bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center flex-1 relative">
              {imageLoadError ? (
                <div className="flex flex-col items-center gap-3 text-center px-4">
                  <p className="text-sm text-red-600 font-medium">Image failed to load</p>
                  <p className="text-xs text-gray-500">Credits have been refunded</p>
                  <button
                    onClick={handleGenerateImage}
                    className="mt-2 px-3 py-1.5 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700"
                  >
                    Try Again
                  </button>
                </div>
              ) : imageHistory.length > 0 && imageHistory[historyIndex] ? (
                <>
                  <img
                    src={imageHistory[historyIndex].url}
                    alt={getName(object)}
                    className="w-full h-full object-contain"
                    onError={handleImageError}
                  />
                  {generatingImageUid === (object._uid || object.id) && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm text-white font-medium">Generating image...</p>
                      </div>
                    </div>
                  )}
                  {imageHistory[historyIndex].url !== object.referenceImage && generatingImageUid !== (object._uid || object.id) && (
                    <button
                      onClick={() => handleRestoreImage(imageHistory[historyIndex].url)}
                      className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/50 transition-colors"
                      title="Set this as current image"
                    >
                      <span className="px-4 py-2 rounded bg-green-600 text-white text-sm font-medium hover:bg-green-700">
                        ✓ Use This Image
                      </span>
                    </button>
                  )}
                </>
              ) : object.referenceImage ? (
                <>
                  <img
                    src={object.referenceImage}
                    alt={getName(object)}
                    className="w-full h-full object-contain"
                    onError={handleImageError}
                  />
                  {generatingImageUid === (object._uid || object.id) && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm text-white font-medium">Generating image...</p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <p className="text-gray-400 text-sm">No image generated yet</p>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleUploadClick}
                disabled={uploading}
                className="flex-1 px-3 py-2 rounded bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                title="Upload a custom image"
              >
                {uploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span>Upload</span>
                  </>
                )}
              </button>
              <button
                onClick={handleGenerateImage}
                disabled={generatingImageUid === (object._uid || object.id) || uploading}
                className="flex-1 px-3 py-2 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {generatingImageUid === (object._uid || object.id) ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <span>↻ Generate new image - {GENERATION_COST} Credits</span>
                  </>
                )}
              </button>
            </div>
            {uploadError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-xs">
                {uploadError}
              </div>
            )}
          </div>

          {/* Right: Raw JSON */}
          <div className="flex-1 flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-gray-700">Raw JSON</h3>
            <textarea
              value={JSON.stringify(sortObjectKeys(object), null, 2)}
              readOnly
              className="flex-1 border rounded px-3 py-2 text-gray-900 font-mono text-xs bg-gray-50 overflow-auto"
            />
          </div>
        </div>

        {/* Bottom Section: Attributes (full width) */}
        <div className="flex-1 overflow-hidden flex flex-col px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Attributes</h3>
            <button
              onClick={() => openAddAttributeModal({ type: 'root' })}
              className="text-xs px-2 py-1 rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
            >
              + Add Attribute
            </button>
          </div>
          <div ref={attributesScrollRef} className="overflow-y-auto flex-1 text-xs space-y-3 pr-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                  <input
                    type="text"
                    value={getName(formData)}
                    onChange={(e) => handleAttributeChange('name', e.target.value)}
                    className="w-full border rounded px-2 py-1 text-xs text-gray-900"
                  />
                </div>

                {(() => {
                  const filterKeys = [
                    'name', 'Name', 'mainName', 'nickName', 'nickname', 'Alias', 'alias', 'TrueName', 'trueName',
                    'imageUrl', 'referenceImage', 'imageHistory', '_uid', 'id'
                  ];
                  
                  const entries = Object.entries(formData).filter(
                    ([k, v]) => !filterKeys.includes(k) && v !== undefined && v !== null
                  ).sort(([keyA], [keyB]) => keyA.localeCompare(keyB));

                                  const labelize = (s) => s.replace(/([A-Z])/g, ' $1').replace(/^./, (ch) => ch.toUpperCase()).trim();

                  return entries.map(([key, value]) => {
                    // Handle nested objects (like outfit, facial_features, etc)
                    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                      return (
                        <div key={key} data-attribute-key={key} className="border-t pt-2 transition-colors duration-200">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium text-gray-700">{labelize(key)}</p>
                            <button
                              onClick={() => openAddAttributeModal({ type: 'nested', groupKey: key })}
                              className="text-xs px-2 py-1 rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                            >
                              + Add
                            </button>
                          </div>
                          <ul className="space-y-2 ml-2">
                            {Object.entries(value).sort(([keyA], [keyB]) => keyA.localeCompare(keyB)).map(([subKey, subValue]) => (
                              <li key={`${key}.${subKey}`} data-attribute-key={`nested-${key}-${subKey}`} className="text-gray-600 transition-colors duration-200">
                                <label className="block text-xs font-medium text-gray-600 mb-0.5">{labelize(subKey)}</label>
                                {typeof subValue === 'string' ? (
                                  <input
                                    type="text"
                                    value={subValue || ''}
                                    onChange={(e) => handleNestedAttributeChange(key, subKey, e.target.value)}
                                    className="w-full border rounded px-1.5 py-0.5 text-xs text-gray-900"
                                  />
                                ) : (
                                  <textarea
                                    value={typeof subValue === 'string' ? subValue : JSON.stringify(subValue, null, 2)}
                                    onChange={(e) => {
                                      try {
                                        handleNestedAttributeChange(key, subKey, JSON.parse(e.target.value));
                                      } catch (_) {
                                        handleNestedAttributeChange(key, subKey, e.target.value);
                                      }
                                    }}
                                    rows={2}
                                    className="w-full border rounded px-1.5 py-0.5 text-xs text-gray-900 font-mono"
                                  />
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    }
                    
                    // Handle arrays (like variations, alternate_names)
                    if (Array.isArray(value)) {
                      return (
                        <div key={key} data-attribute-key={key} className="border-t pt-2 transition-colors duration-200">
                          <p className="font-medium text-gray-700 mb-2">{labelize(key)}</p>
                          <ul className="space-y-1 ml-2">
                            {value.map((item, idx) => {
                              if (typeof item === 'string') {
                                return (
                                  <li key={idx} className="text-gray-600 text-xs flex items-start gap-2">
                                    <span className="text-gray-400 mt-0.5">•</span>
                                    <input
                                      type="text"
                                      value={item}
                                      onChange={(e) => {
                                        const newArray = [...value];
                                        newArray[idx] = e.target.value;
                                        handleAttributeChange(key, newArray);
                                      }}
                                      className="flex-1 border rounded px-1.5 py-0.5 text-xs text-gray-900"
                                    />
                                  </li>
                                );
                              }
                              
                              // For object items in array (like variations)
                              return (
                                <li key={idx} className="text-gray-600 text-xs border-l-2 border-gray-300 pl-2 py-1">
                                  <div className="flex items-center justify-between mb-1">
                                    <p className="font-medium text-gray-700">{labelize(key)}[{idx}]</p>
                                    <button
                                      onClick={() => openAddAttributeModal({ type: 'variation', variationIndex: idx, groupKey: key })}
                                      className="text-xs px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                                    >
                                      + Add
                                    </button>
                                  </div>
                                  <ul className="space-y-1 ml-2">
                                    {Object.entries(item).sort(([keyA], [keyB]) => keyA.localeCompare(keyB)).map(([subKey, subValue]) => (
                                      <li key={`${idx}.${subKey}`} data-attribute-key={`variation-${key}-${idx}-${subKey}`} className="flex items-start gap-1 transition-colors duration-200">
                                        <span className="text-gray-400 mt-0.5">•</span>
                                        <div className="flex-1">
                                          <label className="block text-xs font-medium text-gray-600 mb-0.5">{labelize(subKey)}</label>
                                          {typeof subValue === 'string' ? (
                                            <input
                                              type="text"
                                              value={subValue || ''}
                                              onChange={(e) => {
                                                const newArray = [...value];
                                                newArray[idx] = { ...newArray[idx], [subKey]: e.target.value };
                                                handleAttributeChange(key, newArray);
                                              }}
                                              className="w-full border rounded px-1.5 py-0.5 text-xs text-gray-900"
                                            />
                                          ) : (
                                            <textarea
                                              value={JSON.stringify(subValue, null, 2)}
                                              onChange={(e) => {
                                                try {
                                                  const newArray = [...value];
                                                  newArray[idx] = { ...newArray[idx], [subKey]: JSON.parse(e.target.value) };
                                                  handleAttributeChange(key, newArray);
                                                } catch (_) {}
                                              }}
                                              rows={2}
                                              className="w-full border rounded px-1.5 py-0.5 text-xs text-gray-900 font-mono"
                                            />
                                          )}
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      );
                    }
                    
                    // Handle simple string values
                    return (
                      <div key={key} data-attribute-key={key} className="border-t pt-2 transition-colors duration-200">
                        <label className="block text-xs font-medium text-gray-600 mb-1">{labelize(key)}</label>
                        <input
                          type="text"
                          value={value || ''}
                          onChange={(e) => handleAttributeChange(key, e.target.value)}
                          className="w-full border rounded px-1.5 py-0.5 text-xs text-gray-900"
                        />
                      </div>
                    );
                  });
                })()}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={handleCancel}
            disabled={!hasChanges}
            className="px-4 py-2 rounded border text-gray-700 hover:bg-gray-50 text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges}
            className="px-4 py-2 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
          >
            Save Changes
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded border text-gray-700 hover:bg-gray-50 text-sm"
          >
            Close
          </button>
        </div>
      </div>

      {/* Add Attribute Modal */}
      {showAddAttributeModal && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowAddAttributeModal(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Add New Attribute</h2>
              <button onClick={() => setShowAddAttributeModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">
                ×
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Attribute Name</label>
                <input
                  type="text"
                  value={newAttributeName}
                  onChange={(e) => setNewAttributeName(e.target.value)}
                  placeholder="e.g., color, size, material"
                  className="w-full border rounded px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddAttribute()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Attribute Value</label>
                <textarea
                  value={newAttributeValue}
                  onChange={(e) => setNewAttributeValue(e.target.value)}
                  placeholder="e.g., blue, large, cotton"
                  rows={3}
                  className="w-full border rounded px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowAddAttributeModal(false)}
                className="px-4 py-2 rounded border text-gray-700 hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAttribute}
                className="px-4 py-2 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700"
              >
                Add Attribute
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Art Style Selection Modal */}
      {showArtStyleModal && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowArtStyleModal(false);
          }
        }}>
          <div className="bg-white rounded-lg shadow-xl w-[95vw] h-[90vh] max-w-3xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-2xl font-semibold text-gray-900">Select Art Style</h2>
              <button onClick={() => setShowArtStyleModal(false)} className="text-gray-500 hover:text-gray-700 text-3xl leading-none">
                ×
              </button>
            </div>

            {/* Art Styles Grid */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {visualStyles.map((style) => {
                  const isDefault = !selectedArtStyle && getDefaultArtStyle()?.id === style.id;
                  const isSelected = selectedArtStyle?.id === style.id;
                  return (
                  <button
                    key={style.id}
                    onClick={() => setSelectedArtStyle(style)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                      isSelected || isDefault
                        ? 'border-indigo-600 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <img
                      src={style.image}
                      alt={style.title}
                      className="w-full h-32 object-cover rounded"
                    />
                    <p className="text-sm font-medium text-gray-900 text-center">{style.title}</p>
                    <p className="text-xs text-gray-600 text-center">{style.description}</p>
                  </button>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowArtStyleModal(false)}
                className="px-4 py-2 rounded border text-gray-700 hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const styleToUse = selectedArtStyle || getDefaultArtStyle();
                  if (styleToUse && onGenerateImage) {
                    onGenerateImage(object._uid || object.id, styleToUse.value);
                  }
                  setShowArtStyleModal(false);
                  setSelectedArtStyle(null);
                }}
                disabled={generatingImageUid === (object._uid || object.id)}
                className="px-4 py-2 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
              >
                Generate with Style
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}
